import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { portalPageStyles } from '@/styles/portalPageStyles';

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '1rem',
  marginTop: 0,
};

const paragraphStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  marginBottom: '0.75rem',
};

const listStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  paddingLeft: '1.5rem',
  marginBottom: '0.75rem',
};

const screenshotStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '600px',
  border: '1px solid var(--border-color)',
  borderRadius: '0.5rem',
  marginTop: '0.75rem',
  marginBottom: '0.75rem',
};

const captionStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  marginBottom: '1rem',
};

function SmsOptInPage() {
  usePageTitle('SMS Opt-In Process');

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav />

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2.5rem 2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          SMS Opt-In Process
        </h1>
        <p style={{ ...paragraphStyle, marginBottom: '2rem' }}>
          This page documents how parents and guardians opt in to receive SMS notifications
          from Checkademic. SMS is disabled by default — parents must take two explicit steps
          to enable text message notifications.
        </p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Step 1: Provide Phone Number During Registration</h2>
          <p style={paragraphStyle}>
            When creating their account, parents are presented with an optional phone number field.
            A disclosure at this field states:
          </p>
          <blockquote style={{
            borderLeft: '3px solid var(--primary-color)',
            paddingLeft: '1rem',
            margin: '0.75rem 0',
            color: 'var(--text-secondary)',
            fontSize: '0.95rem',
            lineHeight: 1.7,
          }}>
            "Used for SMS digest notifications. Msg &amp; data rates may apply."
            <br />
            with a link to{' '}
            <a href="/sms-terms" style={{ color: 'var(--primary-color)' }}>SMS Terms</a>.
          </blockquote>
          <p style={paragraphStyle}>
            Providing a phone number is optional and is not required to use the platform.
          </p>
          <img
            src="/images/sms-opt-in/registration-phone-field.png"
            alt="Parent registration form showing the phone number field with SMS disclosure text: 'Optional. Used for SMS digest notifications. Msg and data rates may apply. See SMS Terms.'"
            style={screenshotStyle}
          />
          <p style={captionStyle}>
            Registration form — phone number field with SMS disclosure
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Step 2: Enable SMS in Notification Settings</h2>
          <p style={paragraphStyle}>
            After creating their account, parents navigate to Notification Settings and explicitly
            toggle "SMS Notifications" to enabled. A consent disclosure is displayed:
          </p>
          <blockquote style={{
            borderLeft: '3px solid var(--primary-color)',
            paddingLeft: '1rem',
            margin: '0.75rem 0',
            color: 'var(--text-secondary)',
            fontSize: '0.95rem',
            lineHeight: 1.7,
          }}>
            "By enabling SMS, you consent to receive up to 1 message per day (daily digest)
            or 1 per week (weekly digest). Msg &amp; data rates may apply. Reply STOP to cancel,
            HELP for help."
            <br />
            with links to{' '}
            <a href="/sms-terms" style={{ color: 'var(--primary-color)' }}>SMS Terms</a>
            {' and '}
            <a href="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a>.
          </blockquote>
          <p style={paragraphStyle}>
            SMS notifications are disabled by default. Parents must take explicit action to toggle
            them on. If a parent has not provided a phone number, they are prompted to add one
            before SMS can be enabled.
          </p>
          <img
            src="/images/sms-opt-in/notification-settings-sms.png"
            alt="Notification Settings page showing the SMS Notifications toggle with consent disclosure: 'By enabling SMS, you consent to receive up to 1 message per day (daily digest) or 1 per week (weekly digest). Msg and data rates may apply. Reply STOP to cancel, HELP for help.' with links to SMS Terms and Privacy Policy."
            style={screenshotStyle}
          />
          <p style={captionStyle}>
            Notification Settings — SMS toggle with consent disclosure
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Key Compliance Points</h2>
          <ul style={listStyle}>
            <li>SMS is <strong>disabled by default</strong> — parents must explicitly enable it.</li>
            <li>Consent is <strong>not a condition</strong> of using the platform. All notifications are also available via email.</li>
            <li>Two explicit actions are required: providing a phone number and toggling SMS on.</li>
            <li>Clear disclosures about message frequency and data rates are displayed at both steps.</li>
            <li>STOP and HELP instructions are shown before enabling.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Opt-Out</h2>
          <p style={paragraphStyle}>
            Parents can opt out at any time using either method:
          </p>
          <ul style={listStyle}>
            <li>Reply <strong>STOP</strong> to any message.</li>
            <li>Log in and toggle SMS off in Notification Settings.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Related Policies</h2>
          <ul style={listStyle}>
            <li>
              <a href="/sms-terms" style={{ color: 'var(--primary-color)' }}>SMS Terms</a>
              {' — '}Full SMS program terms including message frequency and sample messages
            </li>
            <li>
              <a href="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a>
              {' — '}How we collect, use, and protect your information
            </li>
            <li>
              <a href="/terms" style={{ color: 'var(--primary-color)' }}>Terms of Service</a>
              {' — '}Terms governing use of Checkademic
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}

export default SmsOptInPage;
